import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileSignature, FileUp, Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Segmentado, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, numero, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { ListaDocumentos } from './Arquivos';
import { FormModeloDocx } from './ModeloDocx';
import { invalidarDocumentos, type Modelo } from './tipos';

type Vista = 'contratos' | 'modelos';

/** Contratos: os gerados de modelo (lista de documentos `contrato`) e os modelos com variáveis do cadastro. */
export function Contratos() {
  const { t } = useTranslation('documentos');
  const s = useSessao();
  const [busca, setBusca] = useSearchParams();
  const [vista, setVista] = useState<Vista>(busca.get('vista') === 'modelos' ? 'modelos' : 'contratos');
  const [gerar, setGerar] = useState(false);
  const [modelo, setModelo] = useState<Modelo | 'novo' | 'novo-docx' | null>(null);
  const trocar = (v: Vista) => {
    setVista(v);
    setBusca(
      (b: URLSearchParams) => {
        if (v === 'modelos') b.set('vista', 'modelos');
        else b.delete('vista');
        return b;
      },
      { replace: true },
    );
  };
  const seletor = <Segmentado<Vista> rotulo={t('abas.contratos')} valor={vista} aoMudar={trocar} opcoes={(['contratos', 'modelos'] as const).map((v) => ({ valor: v, rotulo: t(`vista.${v}`) }))} />;
  const colunas: Coluna<Modelo>[] = [
    { chave: 'nome', rotulo: t('campos.nome'), principal: true, render: (m) => <span className="font-medium">{textoLivre(m.nome)}</span> },
    { chave: 'descricao', rotulo: t('campos.descricao'), render: (m) => (m.descricao ? textoLivre(m.descricao) : '-') },
    { chave: 'formato', rotulo: t('docx.formato'), render: (m) => <Selo cor={m.formato === 'docx' ? 'ciano' : 'neutro'}>{m.formato === 'docx' ? t('docx.formatos.docx', { versao: m.versao }) : t('docx.formatos.texto')}</Selo> },
    { chave: 'contratos', rotulo: t('campos.contratos'), alinhar: 'direita', mono: true, render: (m) => numero(m.contratos) },
    { chave: 'ativo', rotulo: t('campos.ativo'), render: (m) => <Selo cor={m.ativo ? 'verde' : 'neutro'}>{m.ativo ? t('campos.ativo') : '-'}</Selo> },
    { chave: 'updated_at', rotulo: t('campos.atualizado'), render: (m) => data(m.updated_at) },
  ];
  return (
    <>
      <div className="mb-4">{seletor}</div>
      {vista === 'contratos' ? (
        <ListaDocumentos
          tipo="contrato"
          tela="documentos.contratos"
          vazio={t('vazio.contratos')}
          acao={
            s.pode('documentos.criar') && (
              <AcaoPrimaria>
                <Botao variante="primario" icone={<FileSignature className="size-4" />} onClick={() => setGerar(true)}>
                  {t('acoes.gerarContrato')}
                </Botao>
              </AcaoPrimaria>
            )
          }
        />
      ) : (
        <>
          {s.pode('documentos.configurar') && (
            <AcaoPrimaria>
              <Botao icone={<FileUp className="size-4" />} onClick={() => setModelo('novo-docx')}>
                {t('docx.novo')}
              </Botao>
              <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setModelo('novo')}>
                {t('acoes.novoModelo')}
              </Botao>
            </AcaoPrimaria>
          )}
          <Tabela<Modelo>
            tela="documentos.modelos"
            caminho="/documentos/modelos"
            chaveConsulta={['documentos', 'modelos']}
            colunas={colunas}
            aoClicar={(m) => setModelo(m)}
            buscaNaUrl
            vazio={<Vazio texto={t('vazio.modelos')} />}
          />
        </>
      )}
      {gerar && <FormGerar aoFechar={() => setGerar(false)} aoGerar={(id) => setBusca((b: URLSearchParams) => (b.set('id', id), b), { replace: true })} />}
      {modelo && (modelo === 'novo-docx' || (modelo !== 'novo' && modelo.formato === 'docx')) ? (
        <FormModeloDocx key={modelo === 'novo-docx' ? modelo : `${modelo.id}:${modelo.versao}`} modelo={modelo === 'novo-docx' ? null : modelo} aoFechar={() => setModelo(null)} aoCriar={(m) => setModelo(m)} />
      ) : (
        modelo && <FormModelo modelo={modelo === 'novo' ? null : modelo} aoFechar={() => setModelo(null)} />
      )}
    </>
  );
}

/** GerarContratoDeModelo: modelo ativo e o cliente; o PDF sai ligado ao cliente e a ficha abre em seguida. */
function FormGerar({ aoFechar, aoGerar }: { aoFechar: () => void; aoGerar: (id: string) => void }) {
  const { t } = useTranslation('documentos');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useMemo(() => crypto.randomUUID(), []);
  const modelos = useQuery({ queryKey: ['documentos', 'modelos', 'ativos', 'texto'], queryFn: () => get<{ itens: Modelo[] }>('/documentos/modelos', { ativo: 'true', formato: 'texto', limite: 100 }) });
  const campos: DefCampo[] = [
    { nome: 'modelo_id', rotulo: t('campos.modelo'), tipo: 'selecao', obrigatorio: true, opcoes: (modelos.data?.itens ?? []).map((m) => ({ valor: m.id, rotulo: m.nome })) },
    { nome: 'parceiro_id', rotulo: t('campos.parceiro'), tipo: 'busca', obrigatorio: true, busca: { caminho: '/cadastros/parceiros', params: { operacao: true }, rotulo: (i) => String(i.nome ?? '') } },
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', ajuda: t('campos.nomeAjuda') },
    { nome: 'tags', rotulo: t('campos.tags'), tipo: 'tags' },
  ];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('acoes.gerarContrato')}
      rodape={
        <Botao variante="primario" type="submit" form="form-gerar" icone={<FileSignature className="size-4" />}>
          {t('acoes.gerarContrato')}
        </Botao>
      }
    >
      {modelos.isPending ? (
        <Carregando />
      ) : modelos.isError ? (
        <Erro erro={modelos.error} aoTentar={() => modelos.refetch()} />
      ) : !modelos.data.itens.length ? (
        <Vazio texto={t('vazio.modelos')} />
      ) : (
        <Formulario
          id="form-gerar"
          campos={campos}
          inicial={{ tags: [] }}
          aoEnviar={async (v) => {
            await post('/documentos/contratos', { id, modelo_id: v.modelo_id, parceiro_id: v.parceiro_id, nome: String(v.nome ?? '').trim() || undefined, tags: Array.isArray(v.tags) ? v.tags : [] }, { 'Idempotency-Key': id });
            await invalidarDocumentos(qc);
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
            aoGerar(id);
          }}
        />
      )}
    </Drawer>
  );
}

/** Modelo de contrato (configuração do workspace): nome, descrição, texto com as variáveis e ativo. */
function FormModelo({ modelo, aoFechar }: { modelo: Modelo | null; aoFechar: () => void }) {
  const { t } = useTranslation('documentos');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [remover, setRemover] = useState(false);
  const id = useMemo(() => modelo?.id ?? crypto.randomUUID(), [modelo]);
  const podeEditar = s.pode('documentos.configurar');
  const variaveis = useQuery({ queryKey: ['documentos', 'variaveis'], queryFn: () => get<{ itens: string[] }>('/documentos/variaveis') });
  const excluir = useMutation({
    mutationFn: () => del(`/documentos/modelos/${id}`),
    onSuccess: async () => {
      await invalidarDocumentos(qc);
      aoFechar();
    },
    onError: (e) => {
      setRemover(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, desabilitado: !podeEditar },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'texto', desabilitado: !podeEditar },
    { nome: 'corpo', rotulo: t('campos.corpo'), tipo: 'area', obrigatorio: true, ajuda: t('campos.corpoAjuda'), desabilitado: !podeEditar },
    { nome: 'ativo', rotulo: t('campos.ativo'), tipo: 'booleano', desabilitado: !podeEditar },
  ];
  return (
    <>
      <Drawer
        aberto={!remover}
        aoFechar={aoFechar}
        largura="lg"
        titulo={modelo ? textoLivre(modelo.nome) : t('acoes.novoModelo')}
        rodape={
          podeEditar ? (
            <>
              {modelo && (
                <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setRemover(true)}>
                  {t('acoes.excluir')}
                </Botao>
              )}
              <Botao variante="primario" type="submit" form="form-modelo">
                {t('acoes.salvar')}
              </Botao>
            </>
          ) : (
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
          )
        }
      >
        <Formulario
          id="form-modelo"
          campos={campos}
          inicial={modelo ? { nome: modelo.nome, descricao: modelo.descricao ?? '', corpo: modelo.corpo, ativo: modelo.ativo } : { ativo: true }}
          aoEnviar={async (v) => {
            const corpo = { nome: v.nome, descricao: String(v.descricao ?? '').trim() || null, corpo: v.corpo, ativo: !!v.ativo };
            if (modelo) await patch(`/documentos/modelos/${id}`, corpo);
            else await post('/documentos/modelos', { id, ...corpo }, { 'Idempotency-Key': id });
            await invalidarDocumentos(qc);
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
          }}
        >
          <div className="flex flex-col gap-1.5 sm:col-span-6">
            <span className="text-sm font-medium text-tinta-2">{t('campos.variaveis')}</span>
            {variaveis.isError ? (
              <Erro erro={variaveis.error} aoTentar={() => variaveis.refetch()} />
            ) : (
              <ul className="flex min-w-0 flex-wrap gap-1.5">
                {(variaveis.data?.itens ?? []).map((v) => (
                  <li key={v} className="rounded-md border border-borda px-2 py-0.5 font-mono text-xs break-all text-tinta-2">{`{{${v}}}`}</li>
                ))}
              </ul>
            )}
          </div>
        </Formulario>
      </Drawer>
      <Confirmar aberto={remover} aoFechar={() => setRemover(false)} aoConfirmar={() => excluir.mutate()} titulo={t('confirmar.excluirModelo')} texto={t('confirmar.excluirModeloTexto')} perigoso carregando={excluir.isPending} />
    </>
  );
}
